import React, { memo, useMemo, useState, useRef, forwardRef, useEffect, Ref } from 'react';
import { Button, Icon, SageTokens } from '@kajabi/sage-react';
import { PdsButton, PdsIcon } from '@pine-ds/react';
import { starsTone } from '@pine-ds/icons/icons/';

import CopyMagic from 'common/components/CopyMagic';
import { flashError } from 'common/components/FlashMessages';

import useSuggestionsQuery from 'common/components/CopyMagic/hooks/useSuggestionsQuery';
import { ProductEvents } from 'common/components/CopyMagic/CopyMagic.types';
import useChatSession from '../../hooks/useChatSession';
import ConversationAssistantFullscreenError from '../ConversationAssistantFullscreenError';

import './style.scss';

// We need to prefetch suggestions on pages that trigger the Ama open event (CopyMagicEvents.EVENT_NAMES.COMMAND_OPEN).
// Currently it's only in the Builder
const PREFETCH_SUGGESTIONS_ON_PAGES = ['/admin/themes/:id/settings/edit'];

type Props = {
  productArea: string;
  pagePath: string;
  contextToken: string;
  floatingButton?: boolean;
  fullscreen?: boolean;
  prefix?: string;
  suffix?: string;
};

type CopyMagicButtonProps = {
  disabled?: boolean;
  onClick?: () => void;
  isFloating?: boolean;
};

const noop = () => {};
const CopyMagicButton = forwardRef<HTMLButtonElement, CopyMagicButtonProps>(
  ({ isFloating, disabled = false, onClick = noop }, ref) => {
    if (isFloating) {
      return (
        <Button
          ref={ref}
          disabled={disabled}
          color={Button.COLORS.PRIMARY}
          icon="stars-tone"
          onClick={onClick}
          iconOnly
        >
          Launch assistant
        </Button>
      );
    }
    return (
      <PdsButton
        ref={ref as Ref<HTMLPdsButtonElement>}
        disabled={disabled}
        onClick={onClick}
        value="Launch assistant"
        variant="unstyled"
        type="button"
      >
        <PdsIcon icon={starsTone} />
      </PdsButton>
    );
  },
);

const ConversationAssistantButton = ({
  productArea,
  pagePath,
  contextToken,
  floatingButton = true,
  fullscreen = false,
}: Props) => {
  const copyMagicRef = useRef<HTMLButtonElement>(null);
  const { startChatSession, chatSessionData, isChatSessionInProgress } = useChatSession();
  const [buttonClicked, setButtonClicked] = useState(false);

  // Load remote data
  //  when in fullscreen mode
  //  or when a chat session is in progress
  //  or when the button is clicked for the first time
  //  or when on pages that triggers Ama from events (CopyMagicEvents.EVENT_NAMES.COMMAND_OPEN)
  const shouldFetch =
    fullscreen ||
    isChatSessionInProgress() ||
    buttonClicked ||
    PREFETCH_SUGGESTIONS_ON_PAGES.includes(pagePath);

  const { data: response, error } = useSuggestionsQuery({
    contextToken,
    pagePath,
    shouldFetch,
  });
  const { data, meta } = response || {};
  const { category = '', initialPrompt = '', aiConsentRequired = true, siteId = null } = meta || {};
  const { firstOpenedArea = category } = chatSessionData();

  const classNames = [];
  if (floatingButton) classNames.push('copy-magic__conversation-assistant-button--floating');
  if (fullscreen) classNames.push('copy-magic__conversation-assistant-button--fullscreen');

  const className = classNames.join(' ');

  const productEvents: ProductEvents = useMemo(
    () => ({
      prefix: 'Copy Assistant',
      suffix: 'Convo AI',
      payload: {
        'Product Area': productArea,
        'First Opened Area': firstOpenedArea,
        'Page Path': pagePath,
        'Category': category,
        'Site ID': siteId,
        'Expanded': fullscreen,
      },
    }),
    [productArea, firstOpenedArea, pagePath, category, siteId, fullscreen],
  );

  useEffect(() => {
    // trigger a click on the copy magic button when the button is clicked for the first time and data is loaded
    if (buttonClicked && initialPrompt && copyMagicRef.current) {
      copyMagicRef.current.click();
    }
  }, [buttonClicked, initialPrompt]);

  // Error handling when not in fullscreen mode
  useEffect(() => {
    if (error && !fullscreen) {
      flashError({ text: error });
    }
  }, [error, fullscreen]);

  // Error handling when in fullscreen mode
  if (error && fullscreen) {
    return <ConversationAssistantFullscreenError errorMessage={error} />;
  }

  if (!data || !initialPrompt) {
    return (
      <div className={`copy-magic__conversation-assistant-button ${className}`}>
        <CopyMagicButton
          onClick={() => setButtonClicked(true)}
          disabled={!!error}
          isFloating={floatingButton}
        />
      </div>
    );
  }

  return (
    <div className={`copy-magic__conversation-assistant-button ${className}`}>
      <CopyMagic
        trigger={<CopyMagicButton ref={copyMagicRef} isFloating={floatingButton} />}
        fullscreen={fullscreen}
        generatorName={CopyMagic.GENERATORS.CONVERSATION_ASSISTANT}
        productEvents={productEvents}
        initialPrompt={initialPrompt}
        aiConsentRequired={aiConsentRequired}
        suggestions={data}
        contextToken={contextToken}
        onOpen={() => startChatSession({ firstOpenedArea: category })}
      />
    </div>
  );
};

export default memo(ConversationAssistantButton);
